import { Heart, Palette, Pipette, Snowflake, Sun, type LucideIcon } from 'lucide-react';
import {
  ACES_SHARED_FAQ,
  MOCK_ACES,
  type AceFeature,
  type AceFaqItem,
  type AceHowItWorksStep,
  type AceInfo,
  type AceLevel,
  type AceScope,
  type AceStatus,
} from '@/lib/aces-data';
import {
  VAF_AVATAR_FRAMES,
  XAF_AVATAR_FRAMES,
  type AceAvatarFrame,
} from '@/lib/ace-avatar-frames';
import type { PowerInfo } from '@/lib/powers-data';
import type { CmsPublicationStatus } from '@/lib/cms-publication';
import { fixMojibake, fixMojibakeDeep } from '@/lib/fix-mojibake';
import {
  emptyRelatedExplore,
  normalizeRelatedExplore,
  relatedExploreForPersist,
  type RelatedExploreConfig,
} from '@/lib/related-explore';
import {
  emptyPowerPstyleEditorInput,
  pstyleEditorInputToCmsConfig,
  powerToPstyleEditorInput,
  type PowerPstyleEditorInput,
} from '@/lib/power-pstyle-cms';

export const ACES_CMS_STORAGE_KEY = 'xat-help-aces-cms-v1';

export type AceIconId = 'Sun' | 'Heart' | 'Snowflake' | 'Pipette' | 'Palette';

export const ACE_ICON_OPTIONS: { id: AceIconId; label: string; icon: LucideIcon }[] = [
  { id: 'Sun', label: 'Sol', icon: Sun },
  { id: 'Heart', label: 'Coração', icon: Heart },
  { id: 'Snowflake', label: 'Floco de neve', icon: Snowflake },
  { id: 'Pipette', label: 'Conta-gotas', icon: Pipette },
  { id: 'Palette', label: 'Paleta', icon: Palette },
];

const ICON_BY_ID = Object.fromEntries(ACE_ICON_OPTIONS.map((o) => [o.id, o.icon])) as Record<
  AceIconId,
  LucideIcon
>;

const ICON_NAME_BY_COMPONENT = new Map<LucideIcon, AceIconId>(
  ACE_ICON_OPTIONS.map((o) => [o.icon, o.id])
);

export function resolveAceIcon(id: string): LucideIcon {
  return ICON_BY_ID[id as AceIconId] ?? Sun;
}

export function aceIconIdFromAce(ace: AceInfo): AceIconId {
  return ICON_NAME_BY_COMPONENT.get(ace.visual.icon) ?? 'Sun';
}

export type AceCmsRecord = AceInfo & {
  /** ID numérico da API Laravel (seo_entries, admin). */
  id?: number;
  createdAt: string;
  updatedAt: string;
};

export type AcesCmsState = {
  aces: AceCmsRecord[];
};

export type AceEditorInput = {
  slug: string;
  name: string;
  shortDescription: string;
  about: string;
  imageAccent: string;
  visualGradient: string;
  visualGlow: string;
  visualIconId: AceIconId;
  smileyCode: string;
  storePriceXats: number;
  subscriptionDays: number;
  wikiUrl: string;
  powerId: number | '';
  scopes: AceScope[];
  hasLevels: boolean;
  requiresRenewal: boolean;
  isNew: boolean;
  popularity: number;
  releasedAt: string;
  status: AceStatus;
  subscriptionLabel: string;
  renewalLabel: string;
  levelsLabel: string;
  availability: string;
  availabilityLinkLabel: string;
  features: AceFeature[];
  howItWorks: AceHowItWorksStep[];
  faq: AceFaqItem[];
  levels: AceLevel[];
  avatarFramesEnabled: boolean;
  avatarFrames: AceAvatarFrame[];
  pstyleSectionEnabled: boolean;
  pstyle: PowerPstyleEditorInput;
  relatedExplore: RelatedExploreConfig;
  publicationStatus?: CmsPublicationStatus;
};

function nowIso() {
  return new Date().toISOString();
}

function defaultAceAvatarExtras(slug: string): Pick<AceInfo, 'avatarFramesEnabled' | 'avatarFrames'> {
  switch (slug.toLowerCase()) {
    case 'vaf':
      return {
        avatarFramesEnabled: true,
        avatarFrames: VAF_AVATAR_FRAMES.map((f) => ({ ...f })),
      };
    case 'xaf':
      return {
        avatarFramesEnabled: true,
        avatarFrames: XAF_AVATAR_FRAMES.map((f) => ({ ...f })),
      };
    default:
      return { avatarFramesEnabled: false, avatarFrames: [] };
  }
}

function enrichAceWithDefaults(ace: AceInfo): AceInfo {
  const extras = defaultAceAvatarExtras(ace.slug);
  return {
    ...ace,
    avatarFramesEnabled: ace.avatarFramesEnabled ?? extras.avatarFramesEnabled,
    avatarFrames:
      ace.avatarFrames && ace.avatarFrames.length > 0
        ? ace.avatarFrames
        : extras.avatarFrames,
    pstyleSectionEnabled: ace.pstyleSectionEnabled ?? false,
  };
}

function aceToPstyleEditorInput(ace: AceInfo): PowerPstyleEditorInput {
  if (ace.pstyle) {
    return { customizeEnabled: true, config: { ...ace.pstyle } };
  }
  return emptyPowerPstyleEditorInput();
}

function toRecord(ace: AceInfo, timestamps?: { createdAt: string; updatedAt: string }): AceCmsRecord {
  const t = timestamps ?? { createdAt: nowIso(), updatedAt: nowIso() };
  return { ...ace, ...t };
}

export function seedAcesFromMock(): AceCmsRecord[] {
  const t = nowIso();
  return MOCK_ACES.map((a) => toRecord(enrichAceWithDefaults(a), { createdAt: t, updatedAt: t }));
}

/** Persistência — ícone como string (Lucide não serializa em JSON). */
type StoredAce = Omit<AceInfo, 'visual'> & {
  visual: Omit<AceInfo['visual'], 'icon'> & { iconId: string };
  createdAt: string;
  updatedAt: string;
};

function aceToStored(record: AceCmsRecord): StoredAce {
  const { visual, createdAt, updatedAt, ...rest } = record;
  const { icon: _icon, ...visualRest } = visual;
  return {
    ...rest,
    visual: { ...visualRest, iconId: aceIconIdFromAce(record) },
    createdAt,
    updatedAt,
  };
}

function storedToAce(stored: StoredAce): AceCmsRecord {
  const { visual, createdAt, updatedAt, ...rest } = stored;
  const { iconId, ...visualRest } = visual;
  return {
    ...rest,
    visual: { ...visualRest, icon: resolveAceIcon(iconId) },
    createdAt,
    updatedAt,
  };
}

export function mergeAcesCmsWithMockSeed(stored: AceCmsRecord[]): AceCmsRecord[] {
  const bySlug = new Map(stored.map((a) => [a.slug.toLowerCase(), a]));
  const t = nowIso();
  let changed = false;

  for (const mock of MOCK_ACES) {
    const key = mock.slug.toLowerCase();
    const enriched = enrichAceWithDefaults(mock);
    if (!bySlug.has(key)) {
      bySlug.set(key, toRecord(enriched, { createdAt: t, updatedAt: t }));
      changed = true;
      continue;
    }

    const existing = bySlug.get(key)!;
    const extras = defaultAceAvatarExtras(key);
    const needsFrames =
      extras.avatarFramesEnabled &&
      (extras.avatarFrames?.length ?? 0) > 0 &&
      (!existing.avatarFrames?.length || !existing.avatarFramesEnabled);

    if (needsFrames && extras.avatarFrames) {
      bySlug.set(key, {
        ...existing,
        avatarFramesEnabled: true,
        avatarFrames: extras.avatarFrames,
        updatedAt: t,
      });
      changed = true;
    }
  }

  if (!changed) return stored;
  return Array.from(bySlug.values());
}

export function loadAcesCmsState(): AcesCmsState {
  return { aces: [] };
}

export function saveAcesCmsState(state: AcesCmsState) {
  if (typeof window === 'undefined') return;
  const payload = { aces: state.aces.map(aceToStored) };
  localStorage.setItem(ACES_CMS_STORAGE_KEY, JSON.stringify(payload));
}

export function slugifyAceName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .replace(/^-+|-+$/g, '');
}

function parseStorePriceXats(store: string | number): number {
  if (typeof store === 'number') return store;
  const raw = store.trim().replace(/\s*xats?$/i, '').replace(/[^\d]/g, '');
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

/** Campos básicos preenchíveis a partir da API xatBlog (power tipo Ace). */
export function apiPowerToAceEditorPatch(power: PowerInfo): Partial<AceEditorInput> {
  const releasedAt = power.released?.slice(0, 10) || new Date().toISOString().slice(0, 10);
  const smileyCode = power.smilies[0]?.trim().toLowerCase() || power.slug;

  return {
    name: power.name,
    slug: power.slug,
    shortDescription: power.description.short,
    about: power.description.long || power.description.short,
    powerId: power.id,
    smileyCode,
    storePriceXats: parseStorePriceXats(power.prices.store),
    releasedAt,
    wikiUrl: `https://wiki.xat.com/${power.name}`,
  };
}

export function aceToEditorInput(ace: AceInfo): AceEditorInput {
  const enriched = fixMojibakeDeep(enrichAceWithDefaults(ace));
  return {
    slug: enriched.slug,
    name: enriched.name,
    shortDescription: enriched.shortDescription,
    about: enriched.about,
    imageAccent: enriched.imageAccent,
    visualGradient: enriched.visual.gradient,
    visualGlow: enriched.visual.glow,
    visualIconId: aceIconIdFromAce(enriched),
    smileyCode: enriched.smileyCode,
    storePriceXats: enriched.storePriceXats,
    subscriptionDays: enriched.subscriptionDays,
    wikiUrl: enriched.wikiUrl,
    powerId: enriched.powerId ?? '',
    scopes: [...enriched.scopes],
    hasLevels: enriched.hasLevels,
    requiresRenewal: enriched.requiresRenewal,
    isNew: enriched.isNew ?? false,
    popularity: enriched.popularity,
    releasedAt: enriched.releasedAt,
    status: enriched.status,
    subscriptionLabel: enriched.subscriptionLabel,
    renewalLabel: enriched.renewalLabel,
    levelsLabel: enriched.levelsLabel,
    availability: enriched.availability,
    availabilityLinkLabel: enriched.availabilityLinkLabel ?? '',
    features: enriched.features.map((f) => ({ ...f })),
    howItWorks: enriched.howItWorks.map((s) => ({ ...s })),
    faq: enriched.faq.map((f) => ({ ...f })),
    levels: enriched.levels?.map((l) => ({ ...l, perks: [...l.perks] })) ?? [],
    avatarFramesEnabled: enriched.avatarFramesEnabled ?? false,
    avatarFrames: (enriched.avatarFrames ?? []).map((f) => ({ ...f, cdnAlternates: f.cdnAlternates ? [...f.cdnAlternates] : undefined })),
    pstyleSectionEnabled: enriched.pstyleSectionEnabled ?? false,
    pstyle: aceToPstyleEditorInput(enriched),
    relatedExplore: normalizeRelatedExplore(enriched.relatedExplore),
    publicationStatus: enriched.publicationStatus,
  };
}

export function editorInputToAce(input: AceEditorInput, existing?: AceCmsRecord): AceInfo {
  const slug = input.slug.trim().toLowerCase();
  const releasedAt = input.releasedAt.trim() || new Date().toISOString().slice(0, 10);
  const updatedAt = nowIso().slice(0, 10);

  return {
    slug,
    name: input.name.trim(),
    shortDescription: fixMojibake(input.shortDescription.trim()),
    about: fixMojibake(input.about.trim()),
    imageAccent: input.imageAccent.trim() || '#6366f1',
    visual: {
      gradient: input.visualGradient.trim(),
      glow: input.visualGlow.trim(),
      icon: resolveAceIcon(input.visualIconId),
    },
    smileyCode: input.smileyCode.trim().toLowerCase(),
    storePriceXats: Math.max(0, Number(input.storePriceXats) || 0),
    subscriptionDays: Math.max(1, Number(input.subscriptionDays) || 30),
    wikiUrl: input.wikiUrl.trim() || `https://wiki.xat.com/${input.name.trim()}`,
    powerId: input.powerId === '' ? undefined : Number(input.powerId),
    scopes: input.scopes.length > 0 ? input.scopes : ['account'],
    hasLevels: input.hasLevels,
    requiresRenewal: input.requiresRenewal,
    isNew: input.isNew,
    popularity: Math.max(0, Number(input.popularity) || 0),
    releasedAt,
    updatedAt,
    status: input.status,
    subscriptionLabel: fixMojibake(input.subscriptionLabel.trim() || 'Conta'),
    renewalLabel: fixMojibake(input.renewalLabel.trim() || 'Opcional (30 dias)'),
    levelsLabel: fixMojibake(input.levelsLabel.trim() || (input.hasLevels ? 'Com níveis' : 'Sem níveis')),
    availability: fixMojibake(input.availability.trim() || 'Loja xat'),
    availabilityLinkLabel: input.availabilityLinkLabel.trim()
      ? fixMojibake(input.availabilityLinkLabel.trim())
      : undefined,
    features: fixMojibakeDeep(input.features.filter((f) => f.title.trim())),
    howItWorks: fixMojibakeDeep(input.howItWorks.filter((s) => s.title.trim())),
    faq: fixMojibakeDeep(input.faq.filter((f) => f.question.trim())),
    levels: input.hasLevels && input.levels.length > 0 ? fixMojibakeDeep(input.levels) : undefined,
    avatarFramesEnabled: input.avatarFramesEnabled,
    avatarFrames:
      input.avatarFramesEnabled
        ? input.avatarFrames
            .filter((f) => f.id.trim() && f.name.trim())
            .map((f) => ({
              ...f,
              id: f.id.trim().toLowerCase(),
              name: f.name.trim(),
              imageUrl: f.imageUrl?.trim() || undefined,
              shape: f.shape ?? 'square',
            }))
        : [],
    pstyleSectionEnabled: input.pstyleSectionEnabled,
    pstyle: input.pstyleSectionEnabled ? pstyleEditorInputToCmsConfig(input.pstyle) : undefined,
    relatedExplore: relatedExploreForPersist(input.relatedExplore),
    publicationStatus: input.publicationStatus ?? existing?.publicationStatus,
    ...(existing ? {} : {}),
  };
}

export function emptyAceEditorInput(): AceEditorInput {
  const today = new Date().toISOString().slice(0, 10);
  return {
    slug: '',
    name: '',
    shortDescription: '',
    about: '',
    imageAccent: '#6366f1',
    visualGradient: 'linear-gradient(135deg, #818cf8 0%, #6366f1 50%, #4338ca 100%)',
    visualGlow: 'rgba(99, 102, 241, 0.35)',
    visualIconId: 'Sun',
    smileyCode: '',
    storePriceXats: 0,
    subscriptionDays: 30,
    wikiUrl: '',
    powerId: '',
    scopes: ['account'],
    hasLevels: false,
    requiresRenewal: true,
    isNew: false,
    popularity: 0,
    releasedAt: today,
    status: 'active',
    subscriptionLabel: 'Conta',
    renewalLabel: 'Opcional (30 dias)',
    levelsLabel: 'Sem níveis',
    availability: 'Loja xat',
    availabilityLinkLabel: 'Loja xat',
    features: [],
    howItWorks: [],
    faq: [...ACES_SHARED_FAQ],
    levels: [],
    avatarFramesEnabled: false,
    avatarFrames: [],
    pstyleSectionEnabled: false,
    pstyle: emptyPowerPstyleEditorInput(),
    relatedExplore: emptyRelatedExplore(),
  };
}

export function editorInputToRecord(input: AceEditorInput, existing?: AceCmsRecord): AceCmsRecord {
  const t = nowIso();
  const ace = editorInputToAce(input, existing);
  return {
    ...ace,
    createdAt: existing?.createdAt ?? t,
    updatedAt: t,
  };
}
